import type React from "react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@krea/shared/design-system/Button";
import { cn } from "@krea/shared/utils";

import type { ISEBankIDProps } from "../common-types";
import { BankIDAuthenticationState } from "../components/BankIDAuthenticationState";
import { BANKID_TEST_IDS } from "../test-constants";
import { AlternativeAuthOptions } from "./components/AlternativeAuthOptions";
import { AutoStartAuthFlow } from "./components/AutoStartAuthFlow";
import { BankIDLogo } from "./components/BankIDLogo";
import { QRCodeAuthFlow } from "./components/QRCodeAuthFlow";
import { SSNAuthFlow } from "./components/SSNAuthFlow";
import { SEBankIDAuthType, SEBankIDHintCode } from "./types";
import { useSEBankID } from "./useSEBankID";

const BANKID_SCHEMA = "bankid:///";

// Detect if running on iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Detect if on mobile
const isMobile = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

export const SEBankIDFlow: React.FC<ISEBankIDProps> = ({
  authenticationContext,
  identificationContext,
  onSuccess,
  onError,
  onCancel,
  extraData,

  errorComponent,
}) => {
  const [testFlowSSN, setTestFlowSSN] = useState("");
  const [isTestFlowSubmitted, setIsTestFlowSubmitted] = useState(false);
  const { t } = useTranslation();

  const {
    authType,
    state,
    qrCode,
    autoStartToken,
    changeAuthType,
    start,
    cancel,
  } = useSEBankID({
    authenticationContext,
    identificationContext,
    onSuccess,
    onError,
    onCancel,
    testSSN: testFlowSSN,
    extraData,
  });

  // Open BankID app on this device
  const openBankIDOnThisDevice = useCallback((autoStartToken: string) => {
    if (!autoStartToken) return;

    window.location.hash = "#bankid-container";
    let protocol = BANKID_SCHEMA;
    let redirectURL = null;

    if (isIOS()) {
      const isChrome = Boolean(navigator.userAgent.match(/CriOS/));
      const isFirefox = Boolean(navigator.userAgent.match(/FxiOS/));
      const isFacebookApp = Boolean(navigator.userAgent.match(/FBAN|FBAV/i));
      const isInstagramApp = Boolean(navigator.userAgent.match(/Instagram/i));
      const isNativeSafari = navigator.userAgent.includes(
        "(KHTML, like Gecko) Version/",
      );
      const isSafari =
        navigator.userAgent.includes("Safari") &&
        isNativeSafari &&
        !isChrome &&
        !isFirefox;

      if (isChrome) {
        redirectURL = encodeURIComponent("googlechrome://");
      } else if (isFirefox) {
        redirectURL = encodeURIComponent("firefox://");
      } else if (isSafari) {
        redirectURL = encodeURIComponent(
          window.location.href.replace(window.location.hash, "#bankid"),
        );
      } else if (isFacebookApp || isInstagramApp) {
        protocol = "https://app.bankid.com/";
      }
    }

    const query = `?autostarttoken=${autoStartToken}&redirect=${redirectURL}`;
    window.location.href = `${protocol}${query}`;
  }, []);

  // Open BankID app when auto-start token is available
  useEffect(() => {
    if (
      authType === SEBankIDAuthType.AUTO_START &&
      autoStartToken &&
      state.status === "pending"
    ) {
      openBankIDOnThisDevice(autoStartToken);
    }
  }, [authType, autoStartToken, state.status, openBankIDOnThisDevice]);

  const handleChangeAuthType = useCallback(
    (newType: SEBankIDAuthType) => {
      setIsTestFlowSubmitted(false);
      changeAuthType(newType);
    },
    [changeAuthType],
  );

  const handleTestFlowSubmit = () => {
    setIsTestFlowSubmitted(true);
    start(SEBankIDAuthType.SSN);
  };
  const defaultAuthType = isMobile()
    ? SEBankIDAuthType.AUTO_START
    : SEBankIDAuthType.QR_CODE;

  const handleCancel = useCallback(() => {
    cancel();
    setIsTestFlowSubmitted(false);

    if (onCancel) {
      onCancel();
    }
  }, [cancel, onCancel]);

  const handleInitialAuth = () => {
    start(defaultAuthType);
  };

  const getPendingStateTitle = () => {
    if (
      authType === SEBankIDAuthType.QR_CODE &&
      state.status === "pending" &&
      state.hintCode === SEBankIDHintCode.USER_SIGN
    ) {
      return t("bankID.SE.writeSecurityCode");
    }

    if (authType === SEBankIDAuthType.AUTO_START) {
      return t("bankID.SE.autostartTitle");
    }

    return null;
  };

  // Render: Success state
  if (state.status === "success") {
    return null;
  }

  // Render: Error state
  if (state.status === "error") {
    let renderErrorComponent: ReactNode | null = null;

    if (errorComponent) {
      renderErrorComponent =
        typeof errorComponent === "function"
          ? errorComponent({
              error: state.error,
              hintCode: state.hintCode,
            })
          : errorComponent;
    }

    return (
      <div className="flex w-full flex-col items-center">
        {renderErrorComponent ? (
          renderErrorComponent
        ) : (
          <BankIDAuthenticationState error>
            {state.error.message}
          </BankIDAuthenticationState>
        )}
        <Button variant="ghost" className="mt-6" onClick={handleCancel}>
          {t("bankID.common.back")}
        </Button>
      </div>
    );
  }

  // Render: Cancelled state
  if (state.status === "cancelled") {
    return (
      <div className="flex w-full flex-col items-center">
        <h2 className="text-center">{t("bankID.common.canceledTitle")}</h2>
        <BankIDAuthenticationState>
          {t("bankID.common.loginCancelled")}
        </BankIDAuthenticationState>
        <Button variant="ghost" className="mt-6" onClick={handleCancel}>
          {t("bankID.common.back")}
        </Button>
      </div>
    );
  }

  // Render: Pending/Authenticating state
  if (state.status === "pending" || state.status === "authenticating") {
    const isUserSign =
      state.status === "pending" &&
      state.hintCode === SEBankIDHintCode.USER_SIGN;

    const authTypeComponents = {
      [SEBankIDAuthType.QR_CODE]: (
        <QRCodeAuthFlow qrCode={qrCode} isUserSign={isUserSign} />
      ),
      [SEBankIDAuthType.AUTO_START]: <AutoStartAuthFlow />,
      [SEBankIDAuthType.SSN]: (
        <SSNAuthFlow
          ssn={testFlowSSN}
          onSSNChange={setTestFlowSSN}
          onSubmit={handleTestFlowSubmit}
          isSubmitting={isTestFlowSubmitted}
        />
      ),
    };

    return (
      <div className="flex w-full flex-col items-center">
        <h2 className="text-center">{getPendingStateTitle()}</h2>
        {authType ? authTypeComponents[authType] : null}

        {!isUserSign && (
          <AlternativeAuthOptions
            currentAuthType={authType}
            onChangeAuthType={handleChangeAuthType}
          />
        )}

        <Button variant="ghost" className="mt-6" onClick={handleCancel}>
          {t("bankID.SE.abortLogin")}
        </Button>
      </div>
    );
  }

  // Render: Initial/Idle state
  return (
    <div className="flex w-full flex-col items-center">
      <Button
        size="lg"
        className={cn("text-center w-full max-w-[400px] text-lg")}
        onClick={handleInitialAuth}
        data-test-id={BANKID_TEST_IDS.mobileBankIdButton}
      >
        <BankIDLogo color="white" className="size-10" />
        {t("bankID.SE.startBankId")}
      </Button>
    </div>
  );
};
